import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import {
    listCartItems,
    addItemToCart,
    removeItemFromCart,
    removeMultipleItemsFromCart,
    updateItemQuantity,
    CartItems,
} from '../api/Cart';
import { toast } from 'react-toastify';
import { Modal } from 'antd';

export interface CartContextType {
    cartItems: CartItems[];
    totalItems: number;
    isLoading: boolean;
    fetchCartData: (page?: number, limit?: number) => Promise<void>;
    /** @deprecated use fetchCartData */
    fetchCartCount: () => Promise<void>;
    addItem: (productId: number, qty: number, showMsg?: boolean, name?: string) => Promise<void>;
    removeItem: (cartItemId: string, productName?: string) => Promise<void>;
    removeMultipleItems: (cartItemIds: string[]) => Promise<void>;
    confirmRemoveItem: (cartItemId: string, productName?: string) => void;
    confirmRemoveMultipleItems: (cartItemIds: string[]) => void;
    updateItemQty: (name: string, productId: number, qty: number) => Promise<void>;
    qualityControl: (id: number) => { status: boolean; data: CartItems | null };
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [cartItems, setCartItems] = useState<CartItems[]>([]);
    const [totalItems, setTotalItems] = useState<number>(0);
    const [isLoading, setIsLoading] = useState<boolean>(false);


    const fetchCartData = useCallback(async (page: number = 1, limit: number = 10000) => {
        const token = localStorage.getItem('token');
        if (!token) {
            setCartItems([]);
            setTotalItems(0);
            return;
        }

        setIsLoading(true);
        try {
            const response = await listCartItems(page, limit);
            if (response) {
                setCartItems(response.items || []);
                setTotalItems(response.total ?? (response.items?.length ?? 0));
            }
        } catch (error) {
            console.error('Failed to fetch cart data', error);
        } finally {
            setIsLoading(false);
        }
    }, []);



    const addItem = useCallback(async (productId: number, qty: number, showMsg = true, name?: string) => {
        if (showMsg) {
            const productName = name || 'Sản phẩm';
            if (qty > 0) {
                toast.success(`Đã thêm ${qty} ${productName} vào giỏ hàng`, {
                    position: "bottom-right",
                    autoClose: 3000,
                    hideProgressBar: true,
                });
            } else if (qty < 0) {
                toast.info(`Đã giảm ${qty} ${productName} khỏi giỏ hàng`, {
                    position: "bottom-right",
                    autoClose: 3000,
                    hideProgressBar: true,
                });
            }
        }
        await addItemToCart(productId.toString(), qty, showMsg, name);
        await fetchCartData();
    }, [fetchCartData]);

    const removeItem = useCallback(async (cartItemId: string, productName?: string) => {
        if (!cartItemId) return;

        await removeItemFromCart(cartItemId);
        await fetchCartData();
        toast.success(`Sản phẩm "${productName || ''}" đã được xóa khỏi giỏ hàng.`, {
            position: "bottom-right",
            autoClose: 3000,
            hideProgressBar: true,
        });

    }, [fetchCartData]);

    const removeMultipleItems = useCallback(async (cartItemIds: string[]) => {
        await removeMultipleItemsFromCart(cartItemIds);
        await fetchCartData();
    }, [fetchCartData]);

    const changeItemQuantity = useCallback(async (cartItemId: string, qty: number) => {
        await updateItemQuantity(cartItemId, qty);
        await fetchCartData();
    }, [fetchCartData]);

    const confirmRemoveItem = useCallback((cartItemId: string, productName?: string) => {
        const displayName = productName || 'sản phẩm này';
        Modal.confirm({
            title: 'Xóa sản phẩm',
            content: `Bạn có chắc muốn xóa "${displayName}" khỏi giỏ hàng?`,
            okText: 'Xóa',
            cancelText: 'Hủy',
            okButtonProps: { danger: true },
            onOk: async () => { await removeItem(cartItemId, productName); },
        });
    }, [removeItem]);

    const confirmRemoveMultipleItems = useCallback((cartItemIds: string[]) => {
        if (!cartItemIds.length) return;
        Modal.confirm({
            title: 'Xóa sản phẩm đã chọn',
            content: `Bạn có chắc muốn xóa ${cartItemIds.length} sản phẩm đã chọn khỏi giỏ hàng?`,
            okText: 'Xóa',
            cancelText: 'Hủy',
            okButtonProps: { danger: true },
            onOk: async () => { await removeMultipleItems(cartItemIds); },
        });
    }, [removeMultipleItems]);

    const updateItemQty = useCallback(async (name: string, productId: number, qty: number) => {
        const cartItem = cartItems.find(item => item.product_id === productId);
        let quantityChange = qty;

        if (cartItem) {
            quantityChange = qty - cartItem.quantity;
            if (quantityChange === 0) return; // Nếu số lượng không thay đổi, không cần gọi API
            toast.info(`Đang cập nhật số lượng ${name || 'sản phẩm này'}`, {
                toastId: `update-qty-${productId}`,
                updateId: `update-qty-${productId}`,
                position: "bottom-right",
                hideProgressBar: true,
            });
        }
        else {
            toast.info(`Đang thêm ${name || 'sản phẩm này'} vào giỏ...`, {
                toastId: `update-qty-${productId}`,
                updateId: `update-qty-${productId}`,
                position: "bottom-right",
                hideProgressBar: true,
            });
        }
        await addItem(productId, quantityChange, false, name);
        toast.success(`Số lượng ${name || 'sản phẩm này'} đã được cập nhật.`, {
            toastId: `update-qty-${productId}`,
            updateId: `update-qty-${productId}`,
            position: "bottom-right",
            autoClose: 3000,
            hideProgressBar: true,
        });
        await fetchCartData();

    }, [addItem, cartItems, fetchCartData]);

    const qualityControl = (id: number): { status: boolean; data: CartItems | null } => {
        const cartItem = cartItems.find(item => item.product_id === id);
        if (!cartItem) return {
            status: false,
            data: null
        }
        else {
            return {
                status: true,
                data: cartItem
            }
        }
    };

    useEffect(() => {
        fetchCartData();

        const handleCartUpdate = () => fetchCartData();
        window.addEventListener('cart-updated', handleCartUpdate);

        return () => {
            window.removeEventListener('cart-updated', handleCartUpdate);
        };
    }, [fetchCartData]);

    const value: CartContextType = {
        cartItems,
        totalItems,
        isLoading,
        fetchCartData,
        fetchCartCount: fetchCartData,
        addItem,
        removeItem,
        removeMultipleItems,
        confirmRemoveItem,
        confirmRemoveMultipleItems,
        updateItemQty,
        qualityControl,
    };

    return (
        <CartContext.Provider value={value}>
            {children}
        </CartContext.Provider>
    );
};

export const useCart = (): CartContextType => {
    const context = useContext(CartContext);
    if (!context) {
        throw new Error('useCart must be used within a CartProvider');
    }
    return context;
};
